import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useEffect } from 'react';
import { crmService } from '../services/crmService';
import { ClientListItem } from '../models/Client';

export const CRM_QUERY_KEYS = {
  clients: ['crm', 'customers'],
  clientCities: ['crm', 'customers', 'cities'],
  clientDetails: (id) => ['crm', 'customers', id, 'details'],
  activities: (id) => ['crm', 'customers', id, 'activities'],
  activeReminder: (id) => ['crm', 'customers', id, 'reminder'],
};

export const buildReminderClientState = (client, reminder) => ({
  id: client?.id || reminder?.customerUuid,
  companyName: client?.companyName || '',
  hasActiveReminder: Boolean(reminder?.id),
  remindAt: reminder?.remindAt || null,
  reminderId: reminder?.id || null,
});

export const mapActiveReminderToListPatch = (reminder) => {
  if (!reminder?.id) {
    return { hasActiveReminder: false, remindAt: null, reminderId: null };
  }

  return {
    hasActiveReminder: true,
    remindAt: reminder.remindAt,
    reminderId: reminder.id,
  };
};

const patchClientListItem = (queryClient, clientId, patch) => {
  queryClient.setQueriesData({ queryKey: CRM_QUERY_KEYS.clients }, (old) => {
    if (!old?.clients) return old;

    return {
      ...old,
      clients: old.clients.map((client) => {
        if (client.id !== clientId) return client;

        return ClientListItem.fromApi({
          id: client.id,
          companyName: client.companyName,
          city: client.city,
          address: client.address,
          postalCode: client.postalCode,
          status: client.status,
          lastContactDate: client.lastContactDate,
          createdBy: client.createdBy,
          hasActiveReminder: client.hasActiveReminder,
          remindAt: client.remindAt,
          reminderId: client.reminderId,
          ...patch,
        });
      }),
    };
  });
};

const patchClientListLastContactDate = (queryClient, clientId, lastContactDate) => {
  patchClientListItem(queryClient, clientId, { lastContactDate });
};

export const syncClientListLastContact = (queryClient, clientId, manualDate, latestActivityDate) => {
  const effectiveDate = manualDate || latestActivityDate || null;
  if (!effectiveDate) return;

  patchClientListLastContactDate(queryClient, clientId, effectiveDate);
};

export const findLatestReminderActivity = (activities) => {
  if (!activities?.length) return null;

  return activities
    .filter((activity) => activity.type === 'REMINDER')
    .sort((a, b) => new Date(b.remindAt || b.createdAt) - new Date(a.remindAt || a.createdAt))[0] || null;
};

export const syncClientListReminder = (queryClient, clientId, reminder) => {
  patchClientListItem(queryClient, clientId, mapActiveReminderToListPatch(reminder));
};

export const useClients = (params = {}) => {
  return useQuery({
    queryKey: [...CRM_QUERY_KEYS.clients, params],
    queryFn: () => crmService.getClients(params),
    placeholderData: keepPreviousData,
  });
};

export const useClientsForMap = (params = {}, enabled = true) => {
  return useQuery({
    queryKey: [...CRM_QUERY_KEYS.clients, 'map', 500, params],
    queryFn: async () => {
      const result = await crmService.getClients({ page: 0, size: 500, ...params });
      return result.clients;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useCustomerCities = () => {
  return useQuery({
    queryKey: CRM_QUERY_KEYS.clientCities,
    queryFn: () => crmService.getCustomerCities(),
    staleTime: 5 * 60 * 1000,
  });
};

export const useClientDetails = (clientId, enabled = false) => {
  return useQuery({
    queryKey: CRM_QUERY_KEYS.clientDetails(clientId),
    queryFn: () => crmService.getClientDetails(clientId),
    enabled: Boolean(clientId) && enabled,
  });
};

export const useActivities = (clientId, enabled = false) => {
  return useQuery({
    queryKey: CRM_QUERY_KEYS.activities(clientId),
    queryFn: () => crmService.getActivities(clientId),
    enabled: Boolean(clientId) && enabled,
  });
};

export const useActiveReminder = (clientId, enabled = false) => {
  return useQuery({
    queryKey: CRM_QUERY_KEYS.activeReminder(clientId),
    queryFn: () => crmService.getActiveReminder(clientId),
    enabled: Boolean(clientId) && enabled,
    retry: (failureCount, error) => {
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
};

export const useAddActivity = (clientId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityData) => crmService.addActivity(clientId, activityData),
    onSuccess: async (_data, variables) => {
      patchClientListLastContactDate(queryClient, clientId, new Date().toISOString());

      if (variables.type === 'REMINDER') {
        patchClientListItem(queryClient, clientId, {
          hasActiveReminder: true,
          remindAt: variables.remindAt || null,
        });
      }

      queryClient.invalidateQueries({ queryKey: CRM_QUERY_KEYS.clientDetails(clientId) });
      queryClient.invalidateQueries({ queryKey: CRM_QUERY_KEYS.activeReminder(clientId) });
      await queryClient.refetchQueries({ queryKey: CRM_QUERY_KEYS.activities(clientId) });

      if (variables.type === 'REMINDER') {
        const reminder = await queryClient.fetchQuery({
          queryKey: CRM_QUERY_KEYS.activeReminder(clientId),
          queryFn: () => crmService.getActiveReminder(clientId),
        });
        syncClientListReminder(queryClient, clientId, reminder);
      }
    },
  });
};

export const useCreateCustomer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerData) => crmService.createCustomer(customerData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CRM_QUERY_KEYS.clients });
    },
  });
};

export const useSyncClientListLastContact = (clientId, manualDate, latestActivityDate) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!clientId) return;

    syncClientListLastContact(queryClient, clientId, manualDate, latestActivityDate);
  }, [clientId, manualDate, latestActivityDate, queryClient]);
};

export const useSyncClientListCreatedBy = (clientId, createdBy) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!clientId || !createdBy) return;

    patchClientListItem(queryClient, clientId, { createdBy });
  }, [clientId, createdBy, queryClient]);
};

export const useSyncClientListReminder = (clientId, reminder) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!clientId) return;

    syncClientListReminder(queryClient, clientId, reminder);
  }, [clientId, reminder, queryClient]);
};

export const useUpdateCustomer = (clientId) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (customerData) => crmService.updateCustomer(clientId, customerData),
    onSuccess: (_data, variables) => {
      if (Object.prototype.hasOwnProperty.call(variables, 'lastContactDate')) {
        patchClientListLastContactDate(queryClient, clientId, variables.lastContactDate);
      }

      if (Object.prototype.hasOwnProperty.call(variables, 'status')) {
        patchClientListItem(queryClient, clientId, { status: variables.status });
      }

      const listFields = ['companyName', 'city', 'address', 'postalCode'];
      const listPatch = listFields.reduce((patch, field) => {
        if (Object.prototype.hasOwnProperty.call(variables, field)) {
          patch[field] = variables[field];
        }
        return patch;
      }, {});

      if (Object.keys(listPatch).length) {
        patchClientListItem(queryClient, clientId, listPatch);
      }

      queryClient.invalidateQueries({ queryKey: CRM_QUERY_KEYS.clientDetails(clientId) });

      const addressFieldsChanged = ['address', 'postalCode', 'city'].some((field) =>
        Object.prototype.hasOwnProperty.call(variables, field)
      );
      if (addressFieldsChanged) {
        queryClient.invalidateQueries({ queryKey: [...CRM_QUERY_KEYS.clients, 'map'] });
      }
    },
  });
};

export const useMarkReminderAsSeen = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reminderId }) => crmService.markReminderAsSeen(reminderId),
    onSuccess: (_data, { clientId }) => {
      patchClientListItem(queryClient, clientId, mapActiveReminderToListPatch(null));
      queryClient.setQueryData(CRM_QUERY_KEYS.activeReminder(clientId), null);
      queryClient.invalidateQueries({ queryKey: CRM_QUERY_KEYS.clientDetails(clientId) });
      queryClient.invalidateQueries({ queryKey: CRM_QUERY_KEYS.activities(clientId) });
    },
  });
};
